// Push while desyr is closed (28-push.md, "Keeping a device subscribed", "Signing out"): the browser's
// push subscription with desyr's key, this browser's pusher at the chat server (IDs only, with the
// device's quiet windows), and what the worker needs in Cache Storage. Mail pushes were a candidate
// to drop.
import { ClientEvent, type MatrixClient } from 'matrix-js-sdk';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { config, entryFor } from '../core/config';
import { ACCOUNTS_CHANGED, ask, serverOf, type Who } from '../core/session';
import { onSettings, setting } from '../core/settings';
import { readJson, writeJson } from '../core/storage';
import { currentLanguage, onLanguage } from '../i18n';
import { quietWindows, type QuietHours } from './quiet';
import { everyAccount, waiting } from '../accounts/store';
import { authGen } from '../id/store';

export const APP_ID = 'chat.desyr.web';
export const PUSH_CACHE = 'desyr_push';
const DAY = 86_400_000;

/**
 * on, off, blocked by the browser, being set up, the last set-up failed, the browser wants a second tap
 * for the subscription ("Finish setting up", v1.58.79); hidden: no gateway or no push here.
 */
export type PushState = 'on' | 'off' | 'blocked' | 'setting' | 'failed' | 'finish' | 'hidden';
let state: PushState = 'hidden';
const listeners = new Set<() => void>();
const settle = (s: PushState) => ((state = s), listeners.forEach((l) => l()));
/** The state now (tests and the worker's hand-over). */
export const pushStateNow = () => state;
export const usePushState = () => useSyncExternalStore((l) => (listeners.add(l), () => listeners.delete(l)), () => state);

export const canPush = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
export const pushSwitchedOff = () => !!readJson('desyr_push_off');
/** The gateway's notify address for an account's server (config.json `desyrPush`, the server's entry or "*"). */
export const notifyAddress = (a: Who) => entryFor(config().desyrPush, a.base_url, serverOf(a.user_id)) ?? config().desyrPush?.['*'];

/** Per account, the push key this device registered there and when (90: `desyr_push_keys`, v1.58.77). */
const KEYS = 'desyr_push_keys';
type Keys = Record<string, { key: string; at: number }>;
export const pushKeys = (): Keys => readJson<Keys>(KEYS) ?? {};
const keepKey = (userId: string, key: string | undefined) => {
  const { [userId]: _, ...rest } = pushKeys();
  writeJson(KEYS, key ? { ...rest, [userId]: { key, at: Date.now() } } : Object.keys(rest).length ? rest : undefined);
};

/** Every setup step — the worker ready, the subscription, the push key, each `pushers/set` — at most 10 s (v1.58.77). */
export const STEP_MS = 10_000;
const step = <T>(work: Promise<T>): Promise<T> =>
  Promise.race([work, new Promise<never>((_, reject) => setTimeout(() => reject(new Error('push step too slow')), STEP_MS))]);

/** One `pushers/set` at an account's own chat server with its own session. */
async function setPusher(a: Who, body: Record<string, unknown>): Promise<void> {
  const answer = await step(ask(a, '/v3/pushers/set', { method: 'POST', body: JSON.stringify({ app_id: APP_ID, ...body }) }));
  if (!answer.ok) throw Object.assign(new Error('pushers/set'), { status: answer.status });
}

const browser = () => {
  const ua = navigator.userAgent;
  return /Edg\//.test(ua) ? 'Edge' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'a browser';
};
const fromBase64url = (s: string) => Uint8Array.from(atob(s.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0));
const sameKey = (a: ArrayBuffer | null | undefined, b: Uint8Array) => !!a && a.byteLength === b.length && new Uint8Array(a).every((x, i) => x === b[i]);

/** The quiet windows by the person's quiet hours and Do Not Disturb (34). */
export const windowsOf = (client: MatrixClient) =>
  quietWindows(client.getAccountData('chat.desyr.quiet_hours' as never)?.getContent<QuietHours>(), client.getAccountData('chat.desyr.status' as never)?.getContent().status === 'dnd');

/**
 * This browser's subscription, asked for only once push is allowed: there can be none before, and
 * WebKit can hang the whole page on `getSubscription()` while push isn't allowed.
 */
async function subscriptionOf(registration: ServiceWorkerRegistration | undefined): Promise<PushSubscription | null> {
  return registration && Notification.permission === 'granted' ? registration.pushManager.getSubscription() : null;
}

/** The subscription when push is on here (and the browser allows it), made with desyr's key. */
export async function subscription(): Promise<PushSubscription | undefined> {
  if (!canPush() || Notification.permission !== 'granted' || !setting('desktop-notifications') || pushSwitchedOff()) return undefined;
  return (await subscriptionOf(await step(navigator.serviceWorker.ready))) ?? undefined;
}

/** This device's pushers removed at each account's server: the key it recorded there (and `current`), then forgotten. */
async function removePushers(accounts: Who[], current?: string): Promise<void> {
  const keys = pushKeys();
  await Promise.all(
    accounts.map(async (a) => {
      for (const key of new Set([keys[a.user_id]?.key, current].filter((k): k is string => !!k))) await setPusher(a, { kind: null, pushkey: key }).catch(() => {});
      keepKey(a.user_id, undefined);
    }),
  );
}

/**
 * The worker's push records (28, Keeping a device subscribed 6; 06, v1.58.79): one per account, by chat
 * server and user ID, each with the browser's sign-in generation; apart from the media hand-over's sessions.
 */
export interface PushRecord {
  userId: string;
  baseUrl: string;
  accessToken: string;
  notify?: string;
  gen: number;
}
const RECORDS = '/sessions';
const recordOf = (a: Who): PushRecord => ({ userId: a.user_id, baseUrl: a.base_url, accessToken: a.access_token, notify: notifyAddress(a), gen: authGen() });
export async function readRecords(): Promise<PushRecord[]> {
  const hit = await caches.open(PUSH_CACHE).then((c) => c.match(RECORDS));
  return hit ? ((await hit.json()) as PushRecord[]) : [];
}
/** The records written as the accounts are now. */
export async function writeRecords(accounts: Who[]): Promise<void> {
  await caches.open(PUSH_CACHE).then((c) => c.put(RECORDS, new Response(JSON.stringify(accounts.map(recordOf)))));
}
/** An account signed out here: only its record goes, and the worker closes what it showed for it (v1.58.79). */
export async function forgetPushAccount(userId: string): Promise<void> {
  if (typeof caches === 'undefined') return;
  const left = (await readRecords().catch(() => [])).filter((r) => r.userId !== userId);
  await caches.open(PUSH_CACHE).then((c) => c.put(RECORDS, new Response(JSON.stringify(left)))).catch(() => {});
  navigator.serviceWorker?.controller?.postMessage({ type: 'desyr:push-gone', userId });
}

/** The worker ready and desyr's push key fetched, before any tap (v1.58.79); fetched again after a failure. */
export interface Ready {
  registration: ServiceWorkerRegistration;
  key: Uint8Array;
  vapid: string;
}
let preparing: Promise<Ready> | undefined;
/** `fresh`: asked anew (a setup attempt), so a hung earlier one never holds it up. */
export function prepare(fresh = false): Promise<Ready> {
  if (preparing && !fresh) return preparing;
  const made = (async () => {
    const registration = await step(navigator.serviceWorker.ready);
    const answer = await step(fetch('/_desyr/push/vapid', { credentials: 'omit', cache: 'no-store' }));
    if (!answer.ok) throw new Error(`vapid ${answer.status}`);
    const vapid: string = (await answer.json()).publicKey;
    return { registration, key: fromBase64url(vapid), vapid };
  })();
  preparing = made;
  made.catch(() => preparing === made && (preparing = undefined));
  return made;
}
/** The prepared worker and key, once there (the switch and Allow wait for it). */
export function usePushReady(): Ready | undefined {
  const [ready, setReady] = useState<Ready>();
  useEffect(() => {
    if (!canPush()) return;
    let live = true;
    const get = () => void prepare().then((r) => live && setReady(r), () => live && setTimeout(get, 5000));
    get();
    return () => void (live = false);
  }, []);
  return ready;
}

/** The subscription with desyr's key, made now (in a tap: nothing waits on the network before it). */
async function subscribeNow({ registration, key }: Ready, existing?: PushSubscription | null): Promise<PushSubscription> {
  const sub = existing === undefined ? await registration.pushManager.getSubscription() : existing;
  if (sub && sameKey(sub.options.applicationServerKey, key)) return sub;
  if (sub) await sub.unsubscribe();
  return registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key as BufferSource });
}

/**
 * The person's tap on Allow, the switch or "Finish setting up" (28, v1.58.79): the browser's permission
 * request and, granted, the subscription in this tap's own handler, then the pushers. Refused without a
 * new tap: "Finish setting up".
 */
export async function tapToSetUp(client: MatrixClient, ready: Ready): Promise<NotificationPermission> {
  const permission = Notification.permission === 'default' ? await Notification.requestPermission() : Notification.permission;
  if (permission === 'granted' && setting('desktop-notifications') && !pushSwitchedOff())
    try {
      await subscribeNow(ready);
    } catch {
      settle('finish');
      return permission;
    }
  void keepSubscribed(client);
  return permission;
}

/** Accounts this device registered at that are no longer signed in here: their pusher removed where a session is left, forgotten, their notifications closed. */
async function dropGone(accounts: Who[]): Promise<void> {
  const here = new Set(accounts.map((a) => a.user_id));
  const keys = pushKeys();
  await Promise.all(
    Object.keys(keys)
      .filter((id) => !here.has(id))
      .map(async (id) => {
        const left = waiting().find((w) => w.user_id === id);
        if (left) await setPusher(left, { kind: null, pushkey: keys[id].key }).catch(() => {});
        keepKey(id, undefined);
        navigator.serviceWorker?.controller?.postMessage({ type: 'desyr:push-gone', userId: id });
      }),
  );
}

let attempt = 0;

/**
 * Registers this browser for push, or switches its pushers off. Only the latest attempt counts: a hung
 * one never blocks the next, and what an older one finds out later changes nothing (v1.58.77).
 */
export function keepSubscribed(client: MatrixClient): Promise<void> {
  const mine = ++attempt;
  return subscribe(client, () => mine === attempt).catch(() => void (mine === attempt && settle('failed')));
}

async function subscribe(client: MatrixClient, latest: () => boolean): Promise<void> {
  const setState = (s: PushState) => void (latest() && settle(s));
  // Every signed-in circle, the active and the waiting ones, each at its own server (v1.58.77).
  const accounts = everyAccount(client).filter((a) => notifyAddress(a));
  if (!accounts.length || !canPush()) return setState('hidden');
  // Reconciled (v1.58.79): the worker's records match the accounts; circles no longer signed in lose this device's pusher.
  if (latest()) await step(writeRecords(accounts)).catch(() => {});
  await dropGone(accounts);
  const registration = await step(navigator.serviceWorker.ready);
  const existing = await step(subscriptionOf(registration));
  const permitted = Notification.permission === 'granted';
  // Not yet asked: nothing to remove. Taken away ("denied", e.g. in the iPhone's Settings), switched off
  // here, or desktop notifications off: this device's pushers go once at each circle's server (v1.58.77).
  if (Notification.permission === 'default') return setState('off');
  if (!permitted || !setting('desktop-notifications') || pushSwitchedOff()) {
    await removePushers(accounts, existing?.toJSON().keys?.p256dh);
    return setState(Notification.permission === 'denied' ? 'blocked' : 'off');
  }
  const ready = await prepare(true);
  const { vapid } = ready;
  let sub: PushSubscription;
  try {
    sub = await step(subscribeNow(ready, existing));
  } catch (e) {
    // The browser wants the person's tap for it (WebKit): "Finish setting up".
    if ((e as Error)?.name === 'NotAllowedError') return setState('finish');
    throw e;
  }
  const json = sub.toJSON();
  const quiet = windowsOf(client);
  const pushkey = json.keys!.p256dh;
  // "Setting up…" until every circle has its pusher; on only then (v1.58.77).
  if (state !== 'on') setState('setting');
  // Appended, so two circles on one server keep each other's pusher; an older key of this device goes once the new one is there.
  const done = await Promise.allSettled(
    accounts.map(async (a) => {
      await setPusher(a, {
        kind: 'http',
        pushkey,
        app_display_name: 'desyr',
        device_display_name: `desyr in ${browser()}`,
        lang: currentLanguage(),
        append: true,
        data: { url: notifyAddress(a), format: 'event_id_only', endpoint: json.endpoint, auth: json.keys!.auth, quiet },
      });
      const older = pushKeys()[a.user_id]?.key;
      if (older && older !== pushkey) await setPusher(a, { kind: null, pushkey: older }).catch(() => {});
      keepKey(a.user_id, pushkey);
    }),
  );
  if (!latest()) return;
  const failed = done.some((d) => d.status === 'rejected');
  // What the worker needs while desyr is closed: every account's session and the quiet windows.
  const cache = await caches.open(PUSH_CACHE);
  await writeRecords(accounts);
  // For a renewal with no window open (v1.58.77): desyr's push key, this device's name and its current key.
  await cache.put('/pusher', new Response(JSON.stringify({ vapid, device: `desyr in ${browser()}`, pushkey })));
  await cache.put('/quiet', new Response(JSON.stringify(quiet)));
  await cache.put('/language', new Response(JSON.stringify(currentLanguage())));
  setState(failed ? 'failed' : 'on');
}

/** Keeps the device subscribed: now, daily, and when what it depends on changes. */
export function startPush(client: MatrixClient): () => void {
  const again = () => void keepSubscribed(client);
  again();
  const daily = setInterval(again, DAY);
  const offSettings = onSettings(again);
  const offLanguage = onLanguage(again);
  const onData = (ev: { getType(): string }) => ['chat.desyr.quiet_hours', 'chat.desyr.status'].includes(ev.getType()) && again();
  client.on(ClientEvent.AccountData, onData);
  // The browser's permission can change without telling (e.g. after Safari's dialog).
  addEventListener('focus', again);
  // Every time desyr comes to the foreground after more than a minute away, and on every account change,
  // here or in another tab: reconciled at once (28, v1.58.79; the iPhone never fires a renewal event).
  let away = 0;
  const onVisible = () => (document.hidden ? (away = Date.now()) : away && Date.now() - away > 60_000 && again());
  document.addEventListener('visibilitychange', onVisible);
  let queued: ReturnType<typeof setTimeout> | undefined;
  const changed = () => (clearTimeout(queued), (queued = setTimeout(again, 200)));
  const onStorage = (e: StorageEvent) => (e.key === 'desyr_accounts' || e.key === 'desyr_session') && changed();
  addEventListener(ACCOUNTS_CHANGED, changed);
  addEventListener('storage', onStorage);
  const onMessage = (e: MessageEvent) => e.data?.type === 'desyr:push-renewed' && again();
  navigator.serviceWorker?.addEventListener('message', onMessage);
  return () => {
    clearInterval(daily);
    offSettings();
    offLanguage();
    client.off(ClientEvent.AccountData, onData);
    removeEventListener('focus', again);
    document.removeEventListener('visibilitychange', onVisible);
    removeEventListener(ACCOUNTS_CHANGED, changed);
    removeEventListener('storage', onStorage);
    clearTimeout(queued);
    navigator.serviceWorker?.removeEventListener('message', onMessage);
  };
}

/** Signing out the last account: nothing is pushed here any more (the chat server drops its pusher with the session). */
export async function stopPush(): Promise<void> {
  if (!canPush()) return;
  await caches.delete(PUSH_CACHE).catch(() => {});
  const sub = await subscriptionOf(await navigator.serviceWorker.getRegistration());
  await sub?.unsubscribe().catch(() => {});
}
